import React from 'react';
import Field from '../Field';
import AutoTextarea from '../AutoTextarea';

/**
 * audio: an episode attached by URL rather than uploaded to the DAM.
 *
 * Podcast audio is hosted by the podcast host (libsyn behind a podtrac counting
 * redirect) and that counting redirect is the point of the URL, so it is
 * embedded as-is and never copied into our bucket. media_type picks the element
 * the site renders: <audio> or <video>.
 */
export default function AudioEditor({ idPrefix, data, onChange }) {
  const d = data || {};
  const set = (patch) => onChange({ ...d, ...patch });
  const url = d.url || '';
  const isVideo = String(d.media_type || 'audio').toLowerCase() === 'video';
  const playable = /^(https?:)?\/\//i.test(url);

  return (
    <>
      <div className="field-row">
        <Field id={`${idPrefix}-media_type`} label="Media type" hint="Picks the player element.">
          <select id={`${idPrefix}-media_type`} className="select" value={isVideo ? 'video' : 'audio'} onChange={(e) => set({ media_type: e.target.value })}>
            <option value="audio">Audio</option>
            <option value="video">Video</option>
          </select>
        </Field>
        <Field id={`${idPrefix}-duration`} label="Duration" hint="Free text, e.g. 42:07.">
          <input id={`${idPrefix}-duration`} type="text" className="input" value={d.duration || ''} onChange={(e) => set({ duration: e.target.value })} placeholder="42:07" />
        </Field>
      </div>

      <Field
        id={`${idPrefix}-url`}
        label={isVideo ? 'Video URL' : 'Audio URL'}
        required
        hint="The host's URL, counting redirect included — podtrac/libsyn links are embedded, never re-hosted."
      >
        <input id={`${idPrefix}-url`} type="text" className="input mono" value={url} onChange={(e) => set({ url: e.target.value })} placeholder="https://www.podtrac.com/pts/redirect.mp3/traffic.libsyn.com/ep12.mp3" />
      </Field>

      {playable && (
        <div style={{ marginBottom: 12 }}>
          {isVideo ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video controls preload="none" src={url} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 'var(--radius)' }} />
          ) : (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio controls preload="none" src={url} style={{ width: '100%' }} />
          )}
        </div>
      )}

      <Field id={`${idPrefix}-title`} label="Title" hint="Shown as the episode heading.">
        <input id={`${idPrefix}-title`} type="text" className="input" value={d.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="Episode 12 — …" />
      </Field>

      <Field id={`${idPrefix}-description`} label="Description" hint="Markdown show notes. Stored and editable; not yet rendered by the site.">
        <AutoTextarea id={`${idPrefix}-description`} value={d.description || ''} onChange={(e) => set({ description: e.target.value })} minRows={3} />
      </Field>

      <details style={{ marginBottom: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--color-muted)' }}>
          Transcript {d.transcript ? `(${String(d.transcript).length} characters)` : '(empty)'}
        </summary>
        <Field id={`${idPrefix}-transcript`} label="Transcript" hint="Markdown. Stored and editable; not yet rendered by the site." style={{ marginTop: 10 }}>
          <AutoTextarea id={`${idPrefix}-transcript`} value={d.transcript || ''} onChange={(e) => set({ transcript: e.target.value })} minRows={6} />
        </Field>
      </details>
    </>
  );
}
